import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuietMode } from '../components/QuietModeProvider'
import NaturalBackground from '../components/NaturalBackground'
import VirtualCandle from '../components/VirtualCandle'
import EncouragementBlock from '../components/EncouragementBlock'
import GentleBotPrompt from '../components/GentleBotPrompt'
import DopaLogo from '../components/DopaLogo'

const Support = () => {
  const navigate = useNavigate()
  const { isQuietMode, isLowStimMode } = useQuietMode()
  const [activeMode, setActiveMode] = useState<'bot' | 'quiet' | null>(null)
  const [quietState, setQuietState] = useState<'initial' | 'candle' | 'waiting'>('initial')
  const [candleCount, setCandleCount] = useState(0)
  const [showBotPrompt, setShowBotPrompt] = useState(false)
  const [isVoiceCue, setIsVoiceCue] = useState(false)

  if (activeMode === 'bot') {
    // Navigate to the dedicated Dopa Listens route instead of conditionally rendering
    navigate('/dopa-listens')
    return null
  }

  if (activeMode === 'quiet') {
    const handleBotAccept = () => {
      // Navigate to the dedicated Dopa Listens route
      navigate('/dopa-listens')
    }

    const handleBotIgnore = () => {
      if (candleCount >= 3) {
        // After 3 candles, user chooses more quiet time
        setShowBotPrompt(false)
        setIsVoiceCue(false)
        setQuietState('initial') // Reset to initial encouragement
      } else {
        // Light another candle
        setShowBotPrompt(false)
        setIsVoiceCue(false)
        setQuietState('candle')
      }
    }

    const handleCandleComplete = () => {
      const newCount = candleCount + 1
      setCandleCount(newCount)
      setQuietState('waiting')
      
      // Show bot prompt with voice cue after a brief pause
      setTimeout(() => {
        setIsVoiceCue(newCount >= 2) // Voice cue starting from 2nd candle
        setShowBotPrompt(true)
      }, 1500)
    }

    const handleInitialBotIgnore = () => {
      setShowBotPrompt(false)
      setQuietState('candle') // Start first candle
    }

    const handleStartFirstCandle = () => {
      setQuietState('candle')
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-peach-900/40 via-peach-800/30 to-peach-900/40 dark:from-peach-900/40 dark:via-peach-800/30 dark:to-peach-900/40 relative overflow-hidden">
        {/* Dim overlay for low power feel */}
        <div className="absolute inset-0 bg-black/60" />
        
        {/* Subtle stars for nighttime ambiance */}
        <div className="absolute inset-0">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-peach-200 dark:bg-peach-200 rounded-full opacity-30 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 60}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        {/* Back button */}
        <button
          onClick={() => setActiveMode(null)}
          className="absolute top-4 sm:top-8 left-4 sm:left-8 z-30 p-3 sm:p-4 rounded-2xl bg-peach-50/10 dark:bg-peach-900/10 hover:bg-peach-100/20 dark:hover:bg-peach-800/20 
                     border border-peach-100/20 dark:border-peach-700/20 hover:border-peach-200/30 dark:hover:border-peach-600/30 transition-colors duration-500
                     focus:outline-none focus:ring-4 focus:ring-peach-100/20 dark:focus:ring-peach-700/20 backdrop-blur-sm"
        >
          <svg className="w-5 h-5 text-peach-300 dark:text-peach-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Main content */}
        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
          
          {/* Initial state - encouragement block */}
          {quietState === 'initial' && (
            <div className="space-y-8 sm:space-y-12">
              <EncouragementBlock />
              
              {/* Initial bot prompt or start candle option */}
              {!showBotPrompt ? (
                <div className="text-center space-y-4 sm:space-y-6">
                  <button
                    onClick={handleStartFirstCandle}
                    className="px-4 sm:px-6 py-3 bg-peach-50/20 dark:bg-peach-900/20 hover:bg-peach-100/30 dark:hover:bg-peach-800/30 
                               text-peach-600 dark:text-peach-300 rounded-2xl text-xs sm:text-sm font-light border border-peach-100/20 dark:border-peach-700/20 hover:border-peach-200/30 dark:hover:border-peach-600/30
                               transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-peach-100/30 dark:focus:ring-peach-700/30"
                  >
                    Light a candle for quiet time
                  </button>
                  
                  <button
                    onClick={() => setShowBotPrompt(true)}
                    className="block mx-auto text-peach-500 dark:text-peach-400 text-xs sm:text-sm font-light hover:text-peach-600 dark:hover:text-peach-300 transition-colors opacity-60 hover:opacity-80"
                  >
                    Sometimes company helps too...
                  </button>
                </div>
              ) : null}
            </div>
          )}

          {/* Candle burning state */}
          {quietState === 'candle' && (
            <div className="space-y-8 sm:space-y-12">
              <EncouragementBlock />
              <VirtualCandle 
                onComplete={handleCandleComplete}
                candleNumber={candleCount + 1}
              />
            </div>
          )}

          {/* Waiting state - just encouragement */}
          {quietState === 'waiting' && (
            <div className="space-y-8 sm:space-y-12">
              <EncouragementBlock />
              
              {/* Gentle indicator that bot will appear */}
              <div className="text-center">
                <p className="text-peach-500 dark:text-peach-400 text-xs font-light opacity-60">
                  A gentle presence is nearby...
                </p>
              </div>
            </div>
          )}

          {/* Emergency resources - always visible but subtle */}
          <div className="absolute bottom-4 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8">
            <div className="bg-peach-50/10 dark:bg-peach-900/10 backdrop-blur-sm rounded-2xl border border-peach-100/20 dark:border-peach-700/20 p-3 sm:p-4 max-w-xs mx-auto">
              <p className="text-peach-500 dark:text-peach-400 text-xs font-light text-center mb-2">
                If you need immediate help
              </p>
              <div className="space-y-1 text-center">
                <p className="text-peach-500 dark:text-peach-400 text-xs font-light">Crisis Text: 741741</p>
                <p className="text-peach-500 dark:text-peach-400 text-xs font-light">Lifeline: 988</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bot prompt overlay */}
        {showBotPrompt && (
          <GentleBotPrompt
            onAccept={handleBotAccept}
            onIgnore={quietState === 'initial' ? handleInitialBotIgnore : handleBotIgnore}
            isVoiceCue={isVoiceCue}
            candleCount={candleCount}
          />
        )}
      </div>
    )
  }

  return (
    <NaturalBackground variant="ocean">
      <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className={`flex items-center justify-between ${isLowStimMode ? 'mb-12 sm:mb-16' : 'mb-8 sm:mb-12'}`}>
          <button
            onClick={() => navigate('/home')}
            className={`rounded-2xl bg-peach-50/60 hover:bg-peach-100/60 border border-peach-100/40 hover:border-peach-200/50
                       transition-colors duration-500 focus:outline-none focus:ring-4 focus:ring-peach-100/50 ${isLowStimMode ? 'p-4 sm:p-5' : 'p-3 sm:p-4'}`}
          >
            <svg className={`text-peach-500 dark:text-peach-300 ${isLowStimMode ? 'w-6 h-6' : 'w-5 h-5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          {!isQuietMode && (
            <h1 className={`font-light text-peach-600 dark:text-peach-300 ${isLowStimMode ? 'text-xl sm:text-2xl' : 'text-lg sm:text-xl'}`}>
              Gentle Support
            </h1>
          )}
          
          <div className={`${isLowStimMode ? 'w-14 sm:w-16' : 'w-11 sm:w-13'}`}></div>
        </div>

        {/* Welcome Message */}
        {!isQuietMode && (
          <div className={`text-center ${isLowStimMode ? 'mb-12 sm:mb-16' : 'mb-8 sm:mb-12'}`}>
            <p className={`text-peach-500 dark:text-peach-400 font-light leading-relaxed max-w-sm mx-auto ${isLowStimMode ? 'text-base sm:text-lg' : 'text-sm sm:text-base'}`}>
              Choose what feels most gentle right now
            </p>
          </div>
        )}

        {/* Support Options */}
        <div className={`max-w-xs mx-auto ${isLowStimMode ? 'space-y-8 sm:space-y-12' : 'space-y-6 sm:space-y-8'}`}>
          <button
            onClick={() => navigate('/dopa-listens')}
            className={`w-full bg-peach-50/50 hover:bg-peach-100/50 
                       text-peach-700 dark:text-peach-300 font-light rounded-3xl 
                       border border-peach-100/40 hover:border-peach-200/50
                       transition-colors duration-700 ease-out
                       focus:outline-none focus:ring-4 focus:ring-peach-100/50 focus:ring-offset-2 focus:ring-offset-transparent
                       shadow-sm hover:shadow-md ${isLowStimMode ? 'py-8 sm:py-12 px-6 sm:px-8' : 'py-6 sm:py-8 px-4 sm:px-6'}`}
          >
            <div className={`flex flex-col items-center ${isLowStimMode ? 'space-y-3 sm:space-y-4' : 'space-y-2 sm:space-y-3'}`}>
              <DopaLogo size={isLowStimMode ? "lg" : "md"} withText={false} />
              <span className={`font-light ${isLowStimMode ? 'text-lg sm:text-2xl' : 'text-base sm:text-lg'}`}>
                Dopa listens
              </span>
              {!isQuietMode && (
                <p className={`text-peach-500 dark:text-peach-400 font-light leading-relaxed ${isLowStimMode ? 'text-sm sm:text-lg' : 'text-xs sm:text-sm'}`}>
                  Share what's on your heart, or just say a word like 'overwhelmed'. Dopa will listen and respond with care.
                </p>
              )}
            </div>
          </button>

          <button
            onClick={() => setActiveMode('quiet')}
            className={`w-full bg-peach-50/50 hover:bg-peach-100/50 
                       text-peach-700 dark:text-peach-300 font-light rounded-3xl 
                       border border-peach-100/40 hover:border-peach-200/50
                       transition-colors duration-700 ease-out
                       focus:outline-none focus:ring-4 focus:ring-peach-100/50 focus:ring-offset-2 focus:ring-offset-transparent
                       shadow-sm hover:shadow-md ${isLowStimMode ? 'py-8 sm:py-12 px-6 sm:px-8' : 'py-6 sm:py-8 px-4 sm:px-6'}`}
          >
            <div className={`flex flex-col items-center ${isLowStimMode ? 'space-y-3 sm:space-y-4' : 'space-y-2 sm:space-y-3'}`}>
              <svg className={`text-peach-400 dark:text-peach-300 ${isLowStimMode ? 'w-10 h-10 sm:w-12 h-12' : 'w-8 h-8 sm:w-10 h-10'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
              <span className={`font-light ${isLowStimMode ? 'text-lg sm:text-2xl' : 'text-base sm:text-lg'}`}>
                Quiet sanctuary
              </span>
              {!isQuietMode && (
                <p className={`text-peach-500 dark:text-peach-400 font-light leading-relaxed ${isLowStimMode ? 'text-sm sm:text-lg' : 'text-xs sm:text-sm'}`}>
                  A peaceful place to simply be
                </p>
              )}
            </div>
          </button>
        </div>

        {/* Emergency Resources */}
        {!isQuietMode && (
          <div className={`p-6 sm:p-8 bg-peach-50/40 dark:bg-peach-900/30 rounded-3xl border border-peach-100/30 dark:border-peach-700/30 max-w-sm mx-auto ${isLowStimMode ? 'mt-16 sm:mt-20' : 'mt-12 sm:mt-16'}`}>
            <h3 className={`text-peach-600 dark:text-peach-300 font-light text-center mb-4 sm:mb-6 ${isLowStimMode ? 'text-base sm:text-lg' : 'text-sm sm:text-base'}`}>
              If you need immediate help
            </h3>
            <div className={`text-center ${isLowStimMode ? 'space-y-3 sm:space-y-4' : 'space-y-2 sm:space-y-3'}`}>
              <p className={`text-peach-500 dark:text-peach-400 font-light ${isLowStimMode ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`}>
                Crisis Text Line: Text HOME to 741741
              </p>
              <p className={`text-peach-500 dark:text-peach-400 font-light ${isLowStimMode ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`}>
                National Lifeline: 988
              </p>
            </div>
          </div>
        )}

        {/* Bottom Message */}
        {!isQuietMode && (
          <div className={`text-center ${isLowStimMode ? 'mt-12 sm:mt-16' : 'mt-8 sm:mt-12'}`}>
            <p className={`text-peach-500 dark:text-peach-400 font-light leading-relaxed max-w-xs mx-auto ${isLowStimMode ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`}>
              Take a breath. This moment is yours.
            </p>
          </div>
        )}
      </div>
    </NaturalBackground>
  )
}

export default Support